import React from 'react';
import { Row, Col, Card, Table, Tabs, Tab, Badge, Form } from 'react-bootstrap';
import HealthInsurerForm from '../../components/forms/HealthInsurer/HealthInsurerForm';
import { Link } from 'react-router-dom';
import * as ROUTES from '../../lib/routing/frontend';
import * as API_ROUTES from '../../lib/routing/api';
import Helmet from 'react-helmet';
import Alert from 'react-bootstrap/Alert';
import { isSeniorStaff } from '../../lib/helper/authorisation';
import MASpinner from '../../components/common/MASpinner';
import EditableCcsdCodeCost from '../../components/ccsdCodeCost/EditableCcsdCodeCost';
import BulkFeeUpdateTab from '../../components/forms/HealthInsurer/BulkFeeUpdateTab';
import UploadFeeScheduleTab from '../../components/forms/HealthInsurer/UploadFeeScheduleTab';
import { requests } from '../../lib/helper/agent';
import { hydraPageCount } from '../../lib/helper/apiUtils';

const stickyTh = { position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 };

class HealthInsurerSingle extends React.Component {
  state = {
    consultants: [],
    isFetchingConsultants: false,
    hasFetchedConsultants: false,
    consultantFilter: '',
    feeAssuredFilter: 'no',
    costCodeFilter: '',
  };

  handleTabSelect = (key) => {
    const { onFetchCosts } = this.props;
    if (key === 'feeAssuredPrices' && onFetchCosts) {
      onFetchCosts();
    }
    if (key === 'consultants' && !this.state.hasFetchedConsultants) {
      this.fetchConsultants();
    }
  };

  fetchConsultants() {
    const { healthInsurer } = this.props;
    if (!healthInsurer) return;

    this.setState({ isFetchingConsultants: true, hasFetchedConsultants: true });

    const fetchPage = (page, accumulated = []) => {
      requests
        .get(API_ROUTES.CONSULTANT_HEALTH_INSURERS, {
          healthInsurer: healthInsurer.id,
          'consultant.isActive': true,
          'order[consultant.name]': 'ASC',
          itemsPerPage: 250,
          page,
        })
        .then((response) => {
          const items = response['hydra:member'] || [];
          const all = [...accumulated, ...items];
          this.setState({ consultants: all, isFetchingConsultants: all.length < (response['hydra:totalItems'] || 0) });

          const totalPages = hydraPageCount(response);

          if (page < totalPages) {
            fetchPage(page + 1, all);
          } else {
            this.setState({ isFetchingConsultants: false });
          }
        })
        .catch(() => this.setState({ isFetchingConsultants: false }));
    };

    fetchPage(1);
  }

  getFilteredConsultants() {
    const { consultants, consultantFilter, feeAssuredFilter } = this.state;
    return consultants.filter((chi) => {
      if (consultantFilter) {
        const name = chi.consultant?.name || '';
        if (!name.toLowerCase().includes(consultantFilter.toLowerCase())) return false;
      }
      if (feeAssuredFilter === 'yes' && !chi.feeAssured) return false;
      if (feeAssuredFilter === 'no' && chi.feeAssured) return false;
      return true;
    });
  }

  render() {
    const { healthInsurer, defaultCcsdCodeCosts = [], isFetchingDefaultCcsdCodeCosts, isLoadingMore, consultantTypes = [], onCostSaved, hasFetchedCosts } = this.props;
    const { isFetchingConsultants, hasFetchedConsultants, consultantFilter } = this.state;

    if (!healthInsurer) {
      return (
        <Card className="mb-4">
          <Card.Body>
            <Alert variant="info">Loading health insurer...</Alert>
          </Card.Body>
        </Card>
      );
    }

    const filteredConsultants = this.getFilteredConsultants();

    return (
      <div>
        <Helmet>
          <title>{healthInsurer.name} - Health Insurer</title>
        </Helmet>
        <Row>
          <Col>
            <Card className="mb-4">
              <Card.Header className={'card-header--crud'}>
                <div className="d-flex align-items-center">
                  <span className="crud__meta">
                    <Link to={ROUTES.HEALTH_INSURERS.ALL.replace('/:page', '')}>&larr; Back to Health Insurers</Link>
                  </span>
                </div>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col>
                    <h1 className="crud__body-title">
                      {isSeniorStaff() ? 'Edit' : 'View'} Health Insurer ({healthInsurer.name})
                    </h1>
                  </Col>
                </Row>
                <hr />
                <div className="crud__section mb-3">
                  <div className="crud__section-icon">
                    <i className="fas fa-shield-alt" />
                  </div>
                  <Row>
                    <Col sm={12} md={8}>
                      <HealthInsurerForm healthInsurer={healthInsurer} readOnly={true } />
                    </Col>
                    <Col>
                      <div className="crud__section mb-3 bg-white">
                        <div className="crud__section-icon">
                          <i className="fas fa-chart-bar" />
                        </div>
                        <h2 className="crud__section-header">Insurer Statistics</h2>
                        <p>This Health Insurer is related to: </p>
                        <ul>
                          <li>
                            <strong>{healthInsurer?.feeAssuredConsultantCount ?? '0'}</strong> Fee Assured Consultants
                          </li>
                        </ul>
                      </div>
                    </Col>
                  </Row>
                </div>

                <Tabs defaultActiveKey="details" variant="pills" className="mx-1 mb-2" onSelect={this.handleTabSelect}>
                  <Tab eventKey="feeAssuredPrices" title="Fee Assured Costs">
                    <div className="crud__section mb-3">
                      <div className="crud__section-icon">
                        <i className="fas fa-pound-sign" />
                      </div>
                      <h2 className="crud__section-header">Fee Assured CCSD Code Costs</h2>
                      {!hasFetchedCosts ? (
                        <Alert variant="secondary">Select this tab to load fee assured costs.</Alert>
                      ) : defaultCcsdCodeCosts.length === 0 && isFetchingDefaultCcsdCodeCosts ? (
                        <MASpinner msg="Loading fee assured costs..." />
                      ) : defaultCcsdCodeCosts.length === 0 ? (
                        <Alert variant="info">No fee assured costs configured for this insurer.</Alert>
                      ) : (
                        <>
                        {isLoadingMore && (
                          <Alert variant="info" className="py-1 mb-2">Loading more costs... ({defaultCcsdCodeCosts.length} loaded so far)</Alert>
                        )}
                        <Form.Control
                          type="text"
                          placeholder="Filter by code reference or name..."
                          value={this.state.costCodeFilter}
                          onChange={(e) => this.setState({ costCodeFilter: e.target.value })}
                          className="mb-2"
                          style={{ maxWidth: '350px' }}
                        />
                        <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                        <Table striped bordered hover size="sm">
                          <thead>
                            <tr>
                              <th style={stickyTh}>CCSD Code</th>
                              <th style={stickyTh}>Type</th>
                              <th style={stickyTh}>Name</th>
                              <th style={stickyTh}>Consultant Cost</th>
                              <th style={stickyTh}>Anaesthetist Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {defaultCcsdCodeCosts.filter((dcc) => {
                              const f = this.state.costCodeFilter.toLowerCase();
                              if (!f) return true;
                              return (
                                (dcc.ccsdCode?.reference || '').toLowerCase().includes(f) ||
                                (dcc.ccsdCode?.name || '').toLowerCase().includes(f)
                              );
                            }).map((dcc) => {
                              const consultantType = consultantTypes.find((ct) => ct.code === 'C');
                              const anaesthetistType = consultantTypes.find((ct) => ct.code === 'A');
                              return (
                                <tr key={dcc.id}>
                                  <td><Link to={ROUTES.CCSD_CODES.SINGLE.replace(':id', dcc.ccsdCode?.id)}>{dcc.ccsdCode?.reference}</Link></td>
                                  <td>{dcc.ccsdCode?.ccsdCodeType?.name || '-'}</td>
                                  <td>{dcc.ccsdCode?.name || '-'}</td>
                                  {consultantType && (
                                    <EditableCcsdCodeCost
                                      ccsdCode={dcc.ccsdCode}
                                      defaultCcsdCodeCost={dcc}
                                      healthInsurer={healthInsurer}
                                      consultantType={consultantType}
                                      isDefault={true}
                                      onSaved={onCostSaved}
                                    />
                                  )}
                                  {anaesthetistType && (
                                    <EditableCcsdCodeCost
                                      ccsdCode={dcc.ccsdCode}
                                      defaultCcsdCodeCost={dcc}
                                      healthInsurer={healthInsurer}
                                      consultantType={anaesthetistType}
                                      isDefault={true}
                                      onSaved={onCostSaved}
                                    />
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                        </div>
                        </>
                      )}
                    </div>
                  </Tab>
                  <Tab eventKey="consultants" title="Consultants">
                    <div className="crud__section mb-3">
                      <div className="crud__section-icon">
                        <i className="fas fa-user-md" />
                      </div>
                      <h2 className="crud__section-header">Linked Consultants</h2>
                      {!hasFetchedConsultants ? (
                        <Alert variant="secondary">Select this tab to load consultants.</Alert>
                      ) : isFetchingConsultants && filteredConsultants.length === 0 ? (
                        <MASpinner msg="Loading consultants..." />
                      ) : filteredConsultants.length === 0 && !consultantFilter ? (
                        <Alert variant="info">No consultants linked to this insurer.</Alert>
                      ) : (
                        <>
                          {isFetchingConsultants && (
                            <Alert variant="info" className="py-1 mb-2">Loading more consultants... ({this.state.consultants.length} loaded so far)</Alert>
                          )}
                          <div className="d-flex mb-2" style={{ gap: '10px' }}>
                            <Form.Control
                              type="text"
                              placeholder="Filter by name..."
                              value={consultantFilter}
                              onChange={(e) => this.setState({ consultantFilter: e.target.value })}
                              style={{ maxWidth: '300px' }}
                            />
                            <Form.Control
                              as="select"
                              value={this.state.feeAssuredFilter}
                              onChange={(e) => this.setState({ feeAssuredFilter: e.target.value })}
                              style={{ maxWidth: '180px' }}
                            >
                              <option value="all">All</option>
                              <option value="yes">Fee Assured</option>
                              <option value="no">Not Fee Assured</option>
                            </Form.Control>
                          </div>
                          <div style={{ maxHeight: '500px', overflow: 'auto' }}>
                            <Table striped bordered hover size="sm">
                              <thead>
                                <tr>
                                  <th style={stickyTh}>Consultant</th>
                                  <th style={stickyTh}>Fee Assured</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredConsultants.map((chi) => (
                                  <tr key={chi.id}>
                                    <td>
                                      <Link to={ROUTES.CONSULTANTS.SINGLE.replace(':id', chi.consultant?.id)}>
                                        {chi.consultant?.name || 'Unknown'}
                                      </Link>
                                      {(chi.consultant?.consultantTypes || []).map((ct) => (
                                        <Badge key={ct.code} variant={ct.code === 'C' ? 'info' : 'warning'} className="ml-1">{ct.code}</Badge>
                                      ))}
                                    </td>
                                    <td>
                                      {chi.feeAssured ? (
                                        <Badge variant="success">Yes</Badge>
                                      ) : (
                                        <Badge variant="secondary">No</Badge>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                          {!isFetchingConsultants && (
                            <small className="text-muted">{filteredConsultants.length} consultant{filteredConsultants.length !== 1 ? 's' : ''} shown</small>
                          )}
                        </>
                      )}
                    </div>
                  </Tab>
                  {isSeniorStaff() && (
                    <Tab eventKey="bulkFeeUpdate" title="Bulk Fee Update">
                      <div className="crud__section mb-3">
                        <div className="crud__section-icon">
                          <i className="fas fa-sync-alt" />
                        </div>
                        <h2 className="crud__section-header">Bulk Fee Update</h2>
                        <p className="text-muted mb-3">Update costs for multiple non-fee-assured consultants for a specific CCSD code.</p>
                        <BulkFeeUpdateTab healthInsurer={healthInsurer} />
                      </div>
                    </Tab>
                  )}
                  {isSeniorStaff() && (
                    <Tab eventKey="uploadFeeSchedule" title="Upload Fee Schedule">
                      <div className="crud__section mb-3">
                        <div className="crud__section-icon">
                          <i className="fas fa-file-upload" />
                        </div>
                        <h2 className="crud__section-header">Upload Fee Schedule</h2>
                        <p className="text-muted mb-3">Import a complete CCSD code price schedule from a CSV file provided by the insurer.</p>
                        <UploadFeeScheduleTab healthInsurer={healthInsurer} />
                      </div>
                    </Tab>
                  )}
                </Tabs>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

export default HealthInsurerSingle;
